import { CheckCircle, X } from "@phosphor-icons/react";
import BarcodeCameraButton from "@/components/BarcodeCameraButton";
import useMovementForm from "./useMovementForm";

/**
 * Modal de entrada/saída de estoque. Toda a lógica está em `useMovementForm`.
 * Este arquivo é 100% de renderização.
 */
export default function MovementModal({ mode, types, onSaved, onClose }) {
  const f = useMovementForm({ mode, onSaved });
  const accentColor = f.isEntry ? "#00A300" : "#E60000";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass max-w-lg w-full p-6 md:p-8 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="movement-modal">
        <ModalHeader isEntry={f.isEntry} onClose={onClose} />

        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1" data-testid="movement-modal-body">
          <TypeSelect types={types} value={f.type} onChange={f.setType} />
          <ColorPicker type={f.type} colors={f.colors} loading={f.loadingColors} value={f.color} onChange={f.setColor} />
          <QuantityInput isEntry={f.isEntry} value={f.qty} onChange={f.setQty} />
          <NoteInput value={f.note} onChange={f.setNote} />
          <CollaboratorAuth
            code={f.collabCode}
            password={f.collabPassword}
            onCodeChange={f.setCollabCode}
            onPasswordChange={f.setCollabPassword}
          />
          {f.error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2" data-testid="movement-error">{f.error}</div>}
        </div>
        {/* Footer fora da área de scroll — botões sempre visíveis em telas baixas */}
        <Footer isEntry={f.isEntry} saving={f.saving} onClose={onClose} onSubmit={f.submit} accentColor={accentColor} />
      </div>
    </div>
  );
}

function ModalHeader({ isEntry, onClose }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div>
        <div className="overline text-zinc-500">INVENTÁRIO</div>
        <h2 className="font-head text-2xl font-black" style={{ color: isEntry ? "#00A300" : "#E60000" }}>
          {isEntry ? "Entrada de Estoque" : "Saída de Estoque"}
        </h2>
      </div>
      <button onClick={onClose} data-testid="movement-close"><X size={22} /></button>
    </div>
  );
}

function TypeSelect({ types, value, onChange }) {
  return (
    <div>
      <label className="label">Escolha o Produto *</label>
      <select className="input text-base" value={value} onChange={(e) => onChange(e.target.value)} data-testid="movement-type">
        <option value="">— Selecionar tipo —</option>
        {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
      </select>
    </div>
  );
}

function ColorPicker({ type, colors, loading, value, onChange }) {
  let content;
  if (!type) {
    content = <div className="text-sm text-zinc-400 border border-zinc-200 px-3 py-2.5">Escolha o produto primeiro</div>;
  } else if (loading) {
    content = <div className="text-sm text-zinc-400 border border-zinc-200 px-3 py-2.5">Carregando cores…</div>;
  } else if (colors.length === 0) {
    content = (
      <div className="text-sm text-zinc-500 border border-[#FFC000] bg-yellow-50 px-3 py-2.5">
        Sem cores cadastradas para esse tipo. <a className="underline" href="/cadastros/cup-colors" target="_blank" rel="noopener noreferrer">Cadastrar cores</a>
      </div>
    );
  } else {
    content = (
      <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1 border border-zinc-200 p-2" data-testid="movement-colors">
        {colors.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => onChange(c.name)}
            className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors ${value === c.name ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
            data-testid={`movement-color-${c.name}`}
          >
            <span className="w-4 h-4 border border-black" style={{ background: c.hex || "#f4f4f5" }} />
            {c.name}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div>
      <label className="label">Cor do Produto *</label>
      {content}
    </div>
  );
}

function QuantityInput({ isEntry, value, onChange }) {
  return (
    <div>
      <label className="label">Quantidade da {isEntry ? "Entrada" : "Saída"} *</label>
      <input
        type="text"
        inputMode="numeric"
        className="input font-mono text-lg"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder="0"
        data-testid="movement-qty"
      />
    </div>
  );
}

function NoteInput({ value, onChange }) {
  return (
    <div>
      <label className="label">Observação</label>
      <input className="input" value={value} onChange={(e) => onChange(e.target.value)} placeholder="opcional" data-testid="movement-note" />
    </div>
  );
}

function CollaboratorAuth({ code, password, onCodeChange, onPasswordChange }) {
  return (
    <div className="border border-black bg-zinc-50 p-3 space-y-3">
      <div className="overline text-zinc-500">AUTENTICAÇÃO DO COLABORADOR *</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Código do colaborador</label>
          <div className="flex items-stretch gap-1">
            <input className="input font-mono flex-1" inputMode="numeric" value={code} onChange={(e) => onCodeChange(e.target.value)} data-testid="movement-collab-code" />
            <BarcodeCameraButton onResult={(v) => onCodeChange(v)} />
          </div>
        </div>
        <div>
          <label className="label">Senha</label>
          <input type="password" className="input" value={password} onChange={(e) => onPasswordChange(e.target.value)} data-testid="movement-collab-password" />
        </div>
      </div>
    </div>
  );
}

function Footer({ isEntry, saving, onClose, onSubmit, accentColor }) {
  return (
    <div className="flex gap-3 pt-2">
      <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
      <button
        type="button"
        className="btn flex-1"
        style={{ background: accentColor, borderColor: accentColor }}
        onClick={onSubmit}
        disabled={saving}
        data-testid="movement-submit"
      >
        <CheckCircle size={16} weight="bold" /> {saving ? "Salvando..." : `Confirmar ${isEntry ? "Entrada" : "Saída"}`}
      </button>
    </div>
  );
}
